import React from 'react';

import {
  TpHoverInfo,
  TpHoverRenderContext,
  TpHoverRenderInfo,
  ZoomPanCanvas,
} from '../../zoompan-canvas/ZoomPanCanvas';
import { TpZoomPanelInvPointMapInfo } from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpChartProps } from '../interfaces';
import { createDefinition } from './violinplot.Renderer';
import { TpEnSelectionFilterTypes } from '../../../../data/selection/interfaces';
import { TpRectangleSelection } from '../../zoompan-canvas/helpers';
import { convertColumnDataToDisplayString } from '../../helpers/misc';

export function Violinplot({ chartTypeManifest, dataContext, dataBinding, config, widgetContext }: TpChartProps) {
  const widgetGlobalConfig = widgetContext.dashboardContext.widgetGlobalConfig ?? {};
  const { definition, areaPoints, dataTable, strips, stripMap, colInfoValues, colInfoCategories, title } =
    createDefinition(chartTypeManifest, dataContext, dataBinding, config, widgetGlobalConfig);
  const stripCount = strips.length;

  function getHoverInfo(
    mappingInfo: TpZoomPanelInvPointMapInfo,
    logicPositX: number,
    logicPositY: number,
    renderPositX: number,
    renderPositY: number,
  ): TpHoverInfo | null {
    if (mappingInfo.area?.id != 'strips' && mappingInfo.area?.id != 'xaxis') return null;
    const catIdx = Math.floor(logicPositX * stripCount);
    if (catIdx >= 0 && catIdx < stripCount) {
      return {
        dataTableId: dataBinding.dataTableId,
        catColumnId: colInfoCategories.id,
        itemId: strips[catIdx].value,
      };
    }
    return null;
  }

  function getHoverRenderInfo(hoverInfo: TpHoverInfo, context: TpHoverRenderContext): TpHoverRenderInfo | null {
    if (hoverInfo.dataTableId != dataBinding.dataTableId || hoverInfo.catColumnId != colInfoCategories.id) return null;
    const cat = stripMap[hoverInfo.itemId!];
    if (!cat) return null;
    const labels: string[] = [];
    if (context.isActiveChart) {
      labels.push(convertColumnDataToDisplayString(colInfoCategories, cat.value));
      labels.push(
        `Median: ${convertColumnDataToDisplayString(
          colInfoValues,
          cat.densitySelected.q50,
        )} / ${convertColumnDataToDisplayString(colInfoValues, cat.density.q50)}`,
      );
      labels.push(
        `IQR: ${convertColumnDataToDisplayString(
          colInfoValues,
          cat.densitySelected.q75 - cat.densitySelected.q25,
        )} / ${convertColumnDataToDisplayString(colInfoValues, cat.density.q75 - cat.density.q25)}`,
      );
      if (config.displayMean) {
        labels.push(
          `Mean: ${convertColumnDataToDisplayString(
            colInfoValues,
            cat.densitySelected.meanValue,
          )} / ${convertColumnDataToDisplayString(colInfoValues, cat.density.meanValue)}`,
        );
      }
    }
    return {
      area: areaPoints,
      logicPosX: (cat.positionIndex + 0.5) / stripCount,
      logicPosY: cat.densitySelected.q50,
      labels,
    };
  }

  function onRectangleSelection(rectangleSelection: TpRectangleSelection) {
    widgetContext.handleUserSelectionAction({
      type: TpEnSelectionFilterTypes.NUMERICALRANGE,
      dataTableId: dataTable.id,
      colId: colInfoValues.id,
      rangeMin: rectangleSelection.logicYMin,
      rangeMax: rectangleSelection.logicYMax,
    });
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ZoomPanCanvas
        definition={definition}
        getHoverInfo={getHoverInfo}
        getHoverRenderInfo={getHoverRenderInfo}
        onRectangleSelection={onRectangleSelection}
        widgetContext={widgetContext}
        maxZoomXAbsolute={stripCount * 40}
        maxZoomYRelative={30}
        chartTitleInfo={{
          assetId: dataContext.assetId,
          dataType: chartTypeManifest.id,
          title,
        }}
      />
    </div>
  );
}
